import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import styles from '../../../styles/scrollbar.module.css';
import {
	DisabledInteractionFieldset,
	XMCSButton,
} from '../../shared-components';
import { editCreateEnv } from '../../../@xmcloud/core/messages/en';
import { useCreateEditEnvContext } from './hooks/CreateEditEnvContext';
import { useCreateEnvironment } from './hooks/useCreateEnvironment';
import { isEmpty } from 'lodash';
import { requiredEnvFields } from './helper';
import { useEditEnvironment } from './hooks/useEditEnvironment';
import { useOrgProdCheckboxState } from '../../services/organizations';
import { Alert } from '../../shared-components';
import { ModalInteractionBlocker } from '../../shared-components/modals/ModalInteractionBlocker';

const { addCm, addEh, editCm, editEh } = editCreateEnv;

const modalTitle = {
	addCm: addCm,
	editCm: editCm,
	addEh: addEh,
	editEh: editEh,
};

const getKey = (isCreate: boolean, isCm: boolean) => {
	return isCreate ? (isCm ? 'addCm' : 'addEh') : isCm ? 'editCm' : 'editEh';
};

export const MainModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	children: React.ReactNode;
}> = ({ isOpen, onClose, children }) => {
	const [areActionsDisabled, setAreActionsDisabled] = useState(false);
	const modalBodyRef = useRef<HTMLDivElement | null>(null);

	// Reset interaction lock when modal closes
	useEffect(() => {
		if (!isOpen) {
			setAreActionsDisabled(false);
		}
	}, [isOpen]);

	const {
		errors,
		formikHandleSubmit,
		validateForm,
		isLoading: isEditCreateContextLoading,
		isCm,
		isCreate,
		setTouched,
	} = useCreateEditEnvContext();
	const {
		isLoading: isCreateEnvLoading,
		shouldRetry: createShouldRetry,
		onEnvironmentLevelProcess,
		currentEnvironmentId,
	} = useCreateEnvironment();
	const {
		onEditEnvironmentProcess,
		isDisabled,
		isLoading: editEnvIsLoading,
		shouldRetry: editShouldRetry,
	} = useEditEnvironment();

	const key = getKey(isCreate, isCm);

	const isLoading =
		isCreateEnvLoading || isEditCreateContextLoading || editEnvIsLoading;

	const shouldRetry = isCreate ? createShouldRetry : editShouldRetry;

	const hasError = !isEmpty(errors);
	const disabledOnError = hasError && !isCreate;

	const handleModalClose = () => {
		if (areActionsDisabled) {
			return;
		}
		onClose();
	};

	useEffect(() => {
		if (areActionsDisabled && shouldRetry) {
			setAreActionsDisabled(false);
		}
	}, [areActionsDisabled, shouldRetry]);

	const { showCheckbox } = useOrgProdCheckboxState();
	const showProductionCheckbox = isCm && showCheckbox;
	const shouldShowTenantTypeAlert =
		isCreate &&
		isCm &&
		!showProductionCheckbox &&
		Boolean(errors.tenantType);

	const onValidateCreateEnvironment = async () => {
		const validationErrors = await validateForm();
		const hasValidationError = !isEmpty(validationErrors);
		if (hasValidationError && !shouldRetry) {
			setTouched(requiredEnvFields);
			return false;
		}
		return true;
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleModalClose}
			size="2xl"
			scrollBehavior="inside"
			closeOnOverlayClick={!areActionsDisabled}
			closeOnEsc={!areActionsDisabled}
		>
			<ModalOverlay bg="blackAlpha.500" />
			<ModalInteractionBlocker
				isActive={areActionsDisabled}
				scrollContainerRef={modalBodyRef}
			/>
			<form
				data-testid="create-edit-environment-form"
				onSubmit={formikHandleSubmit}
			>
				<ModalContent
					data-testid="main-modal-content"
					aria-busy={areActionsDisabled ? 'true' : 'false'}
				>
					<ModalHeader>{modalTitle[key]}</ModalHeader>
					<ModalCloseButton isDisabled={areActionsDisabled} />
					<ModalBody
						ref={modalBodyRef}
						tabIndex={-1}
						className={styles['custom-scrollbar']}
					>
						<DisabledInteractionFieldset
							disabled={areActionsDisabled}
						>
							{children}
						</DisabledInteractionFieldset>
					</ModalBody>
					<ModalFooter>
						{shouldShowTenantTypeAlert && (
							<Alert
								description={String(errors.tenantType)}
								status="error"
								marginRight={4}
								data-testid="tenant-type-validation-alert"
							/>
						)}
						<XMCSButton
							text="Cancel"
							variant="ghost"
							onClick={handleModalClose}
							size="md"
							isDisabled={areActionsDisabled}
						/>
						<XMCSButton
							text={shouldRetry ? 'Retry' : 'Save'}
							size="md"
							isLoading={isLoading}
							isDisabled={
								isDisabled ||
								disabledOnError ||
								areActionsDisabled
							}
							onClick={async () => {
								if (!(await onValidateCreateEnvironment())) {
									return;
								}
								setAreActionsDisabled(true);
								if (isCreate) {
									onEnvironmentLevelProcess(
										currentEnvironmentId,
									);
									return;
								}
								onEditEnvironmentProcess();
							}}
						/>
					</ModalFooter>
				</ModalContent>
			</form>
		</Modal>
	);
};
